import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Package } from 'lucide-react';
import { 
  getVirtualAccountSyncSettings, 
  updateVirtualAccountSyncSettings,
  getVirtualAccountInstruments,
  updateVirtualAccountInstruments
} from '../../services/api';

const VirtualAccountSyncSettings = ({ virtualAccountId, onSettingsUpdate, isNew = false }) => { 
  const [settings, setSettings] = useState({
    sync_group_filters: false,
    sync_strength_filters: false,
    sync_counter_side: false,
    sync_tactical_counter: false,
    sync_optimal_counter: false,
    sync_upper_bound: false,
    sync_lower_bound: false,
    sync_active_inactive: false,
    sync_trade_fade: false,
    sync_shabbat: false,
    sync_django: false,
    sync_break_out: false,
    sync_source_positions: false,
    sync_timeframe: false,
    sync_short_term_strategy: false,
    sync_override_contract: false,
    sync_mtms_modules: false,
    sync_mtms_trading_modes: false,
  });

  const [availableInstruments, setAvailableInstruments] = useState([]);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [loadingInstruments, setLoadingInstruments] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('sync'); 

  const parameterLabels = {
    sync_group_filters: 'Group Filters',
    sync_strength_filters: 'Strength Filters',
    sync_counter_side: 'Counter Side',
    sync_tactical_counter: 'Tactical Counter',
    sync_optimal_counter: 'Optimal Counter',
    sync_upper_bound: 'Upper Bound',
    sync_lower_bound: 'Lower Bound',
    sync_active_inactive: 'Active/Inactive Status',
    sync_trade_fade: 'Trade/Fade Mode',
    sync_shabbat: 'Shabbat Settings',
    sync_django: 'Django Unchained',
    sync_break_out: 'Break Out/Tactical Start',
    sync_source_positions: 'Source Positions',
    sync_timeframe: 'Timeframe',
    sync_short_term_strategy: 'Short Term Strategy',
    sync_override_contract: 'Override Contract',
    sync_mtms_modules: 'MTMS Module States',
    sync_mtms_trading_modes: 'MTMS Trading Modes',
  };

  useEffect(() => {
    if (virtualAccountId) {
      loadSettings();
      loadInstruments();
    }
  }, [virtualAccountId]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await getVirtualAccountSyncSettings(virtualAccountId);
      if (response) {
        const mappedSettings = {
          sync_group_filters: response.sync_group_filters || false,
          sync_strength_filters: response.sync_strength_filters || false,
          sync_counter_side: response.sync_counter_side || false,
          sync_tactical_counter: response.sync_tactical_counter || false,
          sync_optimal_counter: response.sync_optimal_counter || false,
          sync_upper_bound: response.sync_upper_bound || false,
          sync_lower_bound: response.sync_lower_bound || false,
          sync_active_inactive: response.sync_active_inactive || false,
          sync_trade_fade: response.sync_trade_fade || false,
          sync_shabbat: response.sync_shabbat || false,
          sync_django: response.sync_django || false,
          sync_break_out: response.sync_break_out || false,
          sync_source_positions: response.sync_source_positions || false,
          sync_timeframe: response.sync_timeframe || false,
          sync_short_term_strategy: response.sync_short_term_strategy || false,
          sync_override_contract: response.sync_override_contract || false,
          sync_mtms_modules: response.sync_mtms_modules || false,
          sync_mtms_trading_modes: response.sync_mtms_trading_modes || false,
        };
        setSettings(mappedSettings);
        
        let initialSelected = [];
        if (response.enabled_instruments) {
          initialSelected = response.enabled_instruments.map(instr => 
            `${instr.symbol}${instr.sectype ? `_${instr.sectype}` : ''}`
          );
        }
        setSelectedInstruments(initialSelected);
        
        if (isNew) {
          setSelectedInstruments([]);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInstruments = async () => {
    setLoadingInstruments(true);
    try {
      const response = await getVirtualAccountInstruments(virtualAccountId);
      if (response) {
        setAvailableInstruments(response.available_instruments || []);
        
      }
    } catch (error) {
      console.error('Error loading instruments:', error);
    } finally {
      setLoadingInstruments(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSelectAll = () => {
    const allEnabled = Object.values(settings).every(v => v);
    const newValue = !allEnabled;
    const newSettings = {};
    Object.keys(settings).forEach(key => {
      newSettings[key] = newValue;
    });
    setSettings(newSettings);
  };

  const handleInstrumentToggle = (instrumentKey) => {
    setSelectedInstruments(prev => {
      if (prev.includes(instrumentKey)) {
        return prev.filter(key => key !== instrumentKey);
      } else {
        return [...prev, instrumentKey];
      }
    });
  };

  const handleSelectAllInstruments = () => {
    const allSelected = selectedInstruments.length === availableInstruments.length;
    if (allSelected) {
      setSelectedInstruments([]);
    } else {
      setSelectedInstruments(availableInstruments.map(instr => 
        `${instr.symbol}${instr.sectype ? `_${instr.sectype}` : ''}`
      ));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await updateVirtualAccountSyncSettings(virtualAccountId, settings);
      
      const instrumentUpdates = availableInstruments.map(instr => {
        const instrumentKey = `${instr.symbol}${instr.sectype ? `_${instr.sectype}` : ''}`;
        return {
          virtual_account_id: virtualAccountId,
          symbol: instr.symbol,
          sectype: instr.sectype,
          is_enabled: selectedInstruments.includes(instrumentKey)
        };
      });
      
      await updateVirtualAccountInstruments(virtualAccountId, instrumentUpdates);
      
      setSaveSuccess(true);
      if (onSettingsUpdate) {
        onSettingsUpdate({ ...settings, enabled_instruments: selectedInstruments });
      }
      
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getEnabledCount = () => {
    return Object.values(settings).filter(v => v).length;
  };

  const getInstrumentDisplayName = (instrument) => {
    if (instrument.sectype) {
      return `${instrument.symbol} (${instrument.sectype})`;
    }
    return instrument.symbol;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('sync')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'sync'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Sync Parameters
          </button>
          <button
            onClick={() => setActiveTab('instruments')}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'instruments'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Instruments ({selectedInstruments.length})
          </button>
        </div>
      </div>

      {activeTab === 'sync' && (
        <div>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Synchronization Parameters</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {getEnabledCount()} / {Object.keys(settings).length} enabled
                </span>
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  {Object.values(settings).every(v => v) ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Select which parameters should be synchronized across linked accounts
            </p>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {Object.entries(settings).map(([key, value]) => (
              <div
                key={key}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                  value 
                    ? 'bg-blue-50 border-blue-200 hover:border-blue-300' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleToggle(key)}
              >
                <label className="flex items-center cursor-pointer flex-grow">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={() => {}}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className={`ml-3 font-medium ${
                    value ? 'text-blue-900' : 'text-gray-700'
                  }`}>
                    {parameterLabels[key]}
                  </span>
                </label>
                {value && (
                  <RefreshCw className="w-4 h-4 text-blue-600 animate-spin-slow" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'instruments' && (
        <div>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">Synchronized Instruments</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedInstruments.length} / {availableInstruments.length} selected
                </span>
                <button
                  onClick={handleSelectAllInstruments}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  {selectedInstruments.length === availableInstruments.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Select which instruments should appear in the dashboard and be synchronized
            </p>
          </div>

          {loadingInstruments ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600">Loading instruments...</span>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
              {availableInstruments.length > 0 ? (
                availableInstruments.map((instrument, index) => {
                  const instrumentKey = `${instrument.symbol}${instrument.sectype ? `_${instrument.sectype}` : ''}`;
                  const isSelected = selectedInstruments.includes(instrumentKey);
                  
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                        isSelected 
                          ? 'bg-green-50 border-green-200 hover:border-green-300' 
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleInstrumentToggle(instrumentKey)}
                    >
                      <label className="flex items-center cursor-pointer flex-grow">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500 focus:ring-2"
                        />
                        <span className={`ml-3 font-medium ${
                          isSelected ? 'text-green-900' : 'text-gray-700'
                        }`}>
                          {getInstrumentDisplayName(instrument)}
                        </span>
                      </label>
                      {isSelected && (
                        <RefreshCw className="w-4 h-4 text-green-600 animate-spin-slow" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No compatible instruments found</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Changes will apply to all future synchronizations
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            saveSuccess
              ? 'bg-green-500 text-white hover:bg-green-600'
              : isSaving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {saveSuccess ? (
            <>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Saved!
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </>
          )}
        </button>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default VirtualAccountSyncSettings;